import { type ReactNode, useCallback, useMemo } from 'react';
import { type DefaultTheme, ThemeProvider as StyledComponentThemeProvider } from 'styled-components';
import { getBrandTokens, getThemeTokens } from '@style-dictionary/helpers/modeTokenExtractor';
import type { ThemeMode } from '@style-dictionary/helpers/types/modes';
import { useJavascriptRequestDataModelQuery } from 'autogen/swagger/all/hooks';
import themeMap from 'autogen/theming/themeMap';
import type { Theme } from 'autogen/theming/types/themeTypes';
import { kebabCase, merge } from 'lodash-es';

export default function ThemeProvider({ theme, children }: { theme?: Theme; children: ReactNode }) {
    const { data } = useJavascriptRequestDataModelQuery({ enabled: false });
    const tenant = data?.segmentation.tenant || 'Matas';
    const tenantLower = tenant.toLowerCase();

    const themeTokens: DefaultTheme = useMemo(() => {
        // If no theme prop, return empty so outer theme is preserved.
        if (!theme) return {} as DefaultTheme;

        const baseTheme = themeMap[tenantLower]?.[theme] ?? {};
        const baseThemeV2: DefaultTheme['v2'] = getBrandTokens(tenant);

        if (theme === 'default') {
            return {
                ...baseTheme,
                v2: baseThemeV2
            } as DefaultTheme;
        }

        // Derive sub-theme color tokens; other token groups fall back to base.
        const themeKey = kebabCase(theme) as ThemeMode;
        const subThemeColor = (getThemeTokens(themeKey)[tenantLower] ?? {}) as DefaultTheme['v2']['color'];
        // Merge color tokens so we don't lose base colors that sub-theme does not override.
        const mergedV2: DefaultTheme['v2'] = {
            ...baseThemeV2,
            color: merge({}, baseThemeV2.color, subThemeColor)
        };

        return {
            ...baseTheme,
            v2: mergedV2
        } as DefaultTheme;
    }, [tenant, tenantLower, theme]);

    // Non-mutating merge: create a fresh object combining outer theme and computed vars.
    const mergeWithOuterTheme = useCallback(
        (outerTheme: DefaultTheme | undefined) => {
            return outerTheme ? (merge({}, outerTheme, themeTokens) as DefaultTheme) : themeTokens;
        },
        [themeTokens]
    );

    return <StyledComponentThemeProvider theme={mergeWithOuterTheme}>{children}</StyledComponentThemeProvider>;
}
